import { observer } from 'mobx-react'

import { F0Button, F0Text } from '@factorialco/f0-react'
import { ChevronLeft, ChevronRight, Cross } from '@factorialco/f0-react/icons/app'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import NavigatorHeader from 'modules/documents/components/DocumentNavigator/Header'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'

import Content from './Content'
import ContentF0 from './Content/ContentF0'

export interface NavigatorContentProps {
  document: Document
  documents: Document[]
  index: number
  onCancel: () => void
  onClickBack: () => void
  onClickForward: () => void
}

interface Props {
  saving?: boolean
  disableFilleableConfirm?: boolean
  loading?: boolean
}

export interface DocumentViewerContentProps extends Props, NavigatorContentProps {}

const NavigatorHeaderF0 = ({
  document,
  documents,
  index,
  onCancel,
  onClickBack,
  onClickForward,
}: NavigatorContentProps) => {
  const showCounter = documents.length > 1
  const hidePrevious = index <= 0
  const hideNext = index >= documents.length - 1

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--spacing-s4) var(--spacing-s8)',
        borderBottom: '1px solid var(--border-color-secondary)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
        {!!onClickBack && (
          <F0Button
            variant="ghost"
            size="md"
            hideLabel
            icon={ChevronLeft}
            label="Previous document"
            onClick={onClickBack}
            disabled={hidePrevious}
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--spacing-s8)',
          flex: 1,
          minWidth: 0,
        }}
      >
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <F0Text variant="label" content={document.filename} />
        </div>
        {showCounter && (
          <F0Text variant="description" content={`(${index + 1}/${documents.length})`} />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-s4)',
          flex: '0 0 auto',
        }}
      >
        {!!onClickForward && (
          <F0Button
            variant="ghost"
            size="md"
            hideLabel
            icon={ChevronRight}
            label="Next document"
            onClick={onClickForward}
            disabled={hideNext}
          />
        )}
        <F0Button
          variant="ghost"
          size="md"
          hideLabel
          icon={Cross}
          label="Close"
          onClick={onCancel}
        />
      </div>
    </div>
  )
}

const DocumentPreview = ({
  document,
  documents,
  index,
  onClickBack,
  onClickForward,
  onCancel,
  saving,
  disableFilleableConfirm,
  loading,
}: DocumentViewerContentProps) => {
  const { has_dev_documents_data_collection } = useFeatures(['dev_documents_data_collection'])
  const ContentComponent = has_dev_documents_data_collection ? ContentF0 : Content

  return (
    <>
      {has_dev_documents_data_collection ? (
        <NavigatorHeaderF0
          document={document}
          documents={documents}
          index={index}
          onClickBack={onClickBack}
          onClickForward={onClickForward}
          onCancel={onCancel}
        />
      ) : (
        <NavigatorHeader
          document={document}
          documents={documents}
          index={index}
          onClickBack={onClickBack}
          onClickForward={onClickForward}
          onCancel={onCancel}
        />
      )}
      <ContentComponent
        document={document}
        saving={saving}
        disableFilleableConfirm={disableFilleableConfirm}
        loading={loading}
      />
    </>
  )
}

export default observer(DocumentPreview)
